import React, { useEffect, useRef, useState } from "react";

import {
  formatFractionInput,
  formatPercentageInput,
  isNullOrUndefined,
  isValidNonnegativeNumber,
  isValidPercentageDecimal,
  useGetFootprintingPigletFeedProduction,
  useMutateFootprintingPigletFeedProduction,
  useTranslation,
} from "utils";

import {
  DAIRY_FEED_PRODUCTION_UNITS,
  ENUM_DAIRY_FEED_PRODUCTION_UNITS,
  ENUM_FARM_FOOTPRINTING_DAIRY_FEED_PRODUCTION_CROP_USAGE,
  FARM_FOOTPRINTING_DAIRY_FEED_PRODUCTION_CROP_USAGE,
  PigletFeedProduction,
} from "models";

import {
  AlertBanner,
  Button,
  Card,
  Drawer,
  Flex,
  Form,
  FormInstance,
  InputNumber,
  InputNumberWithUnitSelector,
  KlimRadioGroup,
  Spin,
  Typography,
} from "components";

interface FeedProductionFormData {
  cropUsedForFeed: ENUM_FARM_FOOTPRINTING_DAIRY_FEED_PRODUCTION_CROP_USAGE | null;
  cropYieldTPerHa: number | null;
  dieselConsumptionLHa: number | null;
  energyUsedKwhHa: number | null;
  mineralNitrogenKgHa: number | null;
  mineralPhosphorusKgHa: number | null;
  mineralPotassiumKgHa: number | null;
  nitrogenOrganicFertKgHa: number | null;
  totalLimeAppliedKgCaCo3PerHa: number | null;
  totalPlantProtectionApplied: number | null;
  totalPlantProtectionAppliedUnit: ENUM_DAIRY_FEED_PRODUCTION_UNITS | null;
  totalWaterUsedIrregationLPerHa: number | null;
  soldOffFarmPct: number | null;
  fedToPigletsPct: number | null;
  otherUsesPct: number | null;
}

type FootprintingPigletFeedProductionDrawerProps = {
  farmId: string;
  year: number;
  visible: boolean;
  onClose: () => void;
  feedInformationId: string;
  primaryDataId?: string;
  feedTypeName?: string;
  readOnly?: boolean;
};

export const FootprintingPigletFeedProductionDrawer = ({
  farmId,
  year,
  visible,
  onClose,
  feedInformationId,
  primaryDataId,
  feedTypeName,
  readOnly = false,
}: FootprintingPigletFeedProductionDrawerProps) => {
  const { t } = useTranslation("footprintingPiglet");
  const formRef = useRef<FormInstance>(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState<FeedProductionFormData | null>(null);
  const hasAppliedLoadedValues = useRef(false);

  const { data: feedProductionQuery, isLoading } = useGetFootprintingPigletFeedProduction(feedInformationId ?? "");
  const { mutate: mutateFeedProduction } = useMutateFootprintingPigletFeedProduction(feedInformationId, primaryDataId ?? "", farmId, year, () => {
    setSaving(false);
    onClose();
  });

  const updateFormData = <K extends keyof FeedProductionFormData>(field: K, value: FeedProductionFormData[K]) => {
    setFormData((prev) => ({ ...prev!, [field]: value }));
  };

  const getPlantProtectionError = (value: number | null, unit: ENUM_DAIRY_FEED_PRODUCTION_UNITS | null): string | null => {
    if (isNullOrUndefined(value) || !isValidNonnegativeNumber(value)) {
      return t("pigletFeedProductionForm.error.validNumber");
    }
    if (isNullOrUndefined(unit)) {
      return t("pigletFeedProductionForm.error.unitRequired");
    }
    return null;
  };

  useEffect(() => {
    if (isLoading) return;

    const newFormData: FeedProductionFormData = {
      cropUsedForFeed: feedProductionQuery?.cropUsedForFeed ?? null,
      cropYieldTPerHa: feedProductionQuery?.cropYieldTPerHa ?? null,
      dieselConsumptionLHa: feedProductionQuery?.dieselConsumptionLHa ?? null,
      energyUsedKwhHa: feedProductionQuery?.energyUsedKwhHa ?? null,
      mineralNitrogenKgHa: feedProductionQuery?.mineralNitrogenKgHa ?? null,
      mineralPhosphorusKgHa: feedProductionQuery?.mineralPhosphorusKgHa ?? null,
      mineralPotassiumKgHa: feedProductionQuery?.mineralPotassiumKgHa ?? null,
      nitrogenOrganicFertKgHa: feedProductionQuery?.nitrogenOrganicFertKgHa ?? null,
      totalLimeAppliedKgCaCo3PerHa: feedProductionQuery?.totalLimeAppliedKgCaCo3PerHa ?? null,
      totalPlantProtectionApplied: feedProductionQuery?.totalPlantProtectionApplied ?? null,
      totalPlantProtectionAppliedUnit: feedProductionQuery?.totalPlantProtectionAppliedUnit ?? null,
      totalWaterUsedIrregationLPerHa: feedProductionQuery?.totalWaterUsedIrregationLPerHa ?? null,
      soldOffFarmPct: feedProductionQuery?.soldOffFarmPct ?? null,
      fedToPigletsPct: feedProductionQuery?.fedToPigletsPct ?? null,
      otherUsesPct: feedProductionQuery?.otherUsesPct ?? null,
    };

    setFormData(newFormData);
  }, [feedProductionQuery, isLoading]);

  // The form fields only mount once `formData` is set (see the `isLoading || !formData` guard below), so this
  // effect firing is itself the guarantee that they now exist - no setTimeout needed to "wait for the render".
  // Guarded to run once per mount so it doesn't refight the user's own edits on every later formData change.
  useEffect(() => {
    if (!formData || hasAppliedLoadedValues.current) return;
    hasAppliedLoadedValues.current = true;

    formRef.current?.setFieldsValue({
      cropUsedForFeed: formData.cropUsedForFeed,
      cropYieldTPerHa: formData.cropYieldTPerHa,
      dieselConsumptionLHa: formData.dieselConsumptionLHa,
      energyUsedKwhHa: formData.energyUsedKwhHa,
      mineralNitrogenKgHa: formData.mineralNitrogenKgHa,
      mineralPhosphorusKgHa: formData.mineralPhosphorusKgHa,
      mineralPotassiumKgHa: formData.mineralPotassiumKgHa,
      nitrogenOrganicFertKgHa: formData.nitrogenOrganicFertKgHa,
      totalLimeAppliedKgCaCo3PerHa: formData.totalLimeAppliedKgCaCo3PerHa,
      totalPlantProtectionApplied: formData.totalPlantProtectionApplied,
      totalPlantProtectionAppliedUnit: formData.totalPlantProtectionAppliedUnit,
      totalWaterUsedIrregationLPerHa: formData.totalWaterUsedIrregationLPerHa,
      soldOffFarmPct: formatFractionInput(formData.soldOffFarmPct),
      fedToPigletsPct: formatFractionInput(formData.fedToPigletsPct),
      otherUsesPct: formatFractionInput(formData.otherUsesPct),
    });

    const error = getPlantProtectionError(formData.totalPlantProtectionApplied, formData.totalPlantProtectionAppliedUnit);
    formRef.current?.setFields([{ name: "totalPlantProtectionApplied", errors: error ? [error] : [] }]);
  }, [formData]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = () => {
    setSaving(true);

    const productionData: PigletFeedProduction = {
      feedInformationId,
      primaryDataId,
      ...formData,
    };

    mutateFeedProduction(productionData);
  };

  if (isLoading || !formData) {
    return (
      <Drawer visible={visible} onClose={onClose} title={t("pigletFeedProductionForm.title")}>
        <Flex justify="center" align="center">
          <Spin />
        </Flex>
      </Drawer>
    );
  }

  return (
    <Drawer visible={visible} onClose={onClose} title={t("pigletFeedProductionForm.title")}>
      <Flex vertical justify="space-between" className="footprinting-feed-drawer">
        <Form ref={formRef}>
          <Flex vertical gap={24}>
            {/* Header */}
            <Flex vertical className="margin-bottom-250">
              <Typography variant="h1" className="margin-bottom-200">
                {t("pigletFeedProductionForm.subTitle")}
              </Typography>
              <Flex align="center" gap={12}>
                <Typography variant="paragraph-m" className="drawer-icon-container">
                  {"🌱"}
                </Typography>{" "}
                <Typography variant="h5">{feedTypeName}</Typography>
              </Flex>
            </Flex>

            <Flex vertical gap={16}>
              {/* Crop Yield */}
              <Card className="feed-card padding-100">
                <Typography variant="h5" className="margin-bottom-175">
                  {t("pigletFeedProductionForm.cropYield")}
                </Typography>
                <Form.Item>
                  <InputNumber
                    disabled={readOnly}
                    dataTestId="crop-yield-input"
                    name="cropYieldTPerHa"
                    label={t("pigletFeedProductionForm.question.cropYieldTPerHa")}
                    suffix="t/ha"
                    value={formData.cropYieldTPerHa}
                    initialValue={formData.cropYieldTPerHa}
                    onChange={(v) => updateFormData("cropYieldTPerHa", v)}
                    rules={[
                      {
                        required: true,
                        validator: (_: unknown, v: number) => {
                          if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                            return Promise.reject(new Error(t("pigletFeedProductionForm.error.validNumber")));
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  />
                </Form.Item>
              </Card>

              {/* Energy */}
              <Card className="feed-card padding-100">
                <Typography variant="h5" className="margin-bottom-175">
                  {t("pigletFeedProductionForm.energyUsed")}
                </Typography>

                <AlertBanner
                  className="feed-type-alert-banner margin-bottom-100"
                  description={t("pigletFeedProductionForm.energyAlert")}
                  iconName="tabler/info-circle"
                  type="info-highlighted"
                  iconSize={24}
                  iconAlign="start"
                />

                <Form.Item>
                  <InputNumber
                    disabled={readOnly}
                    dataTestId="diesel-consumption-input"
                    name="dieselConsumptionLHa"
                    label={t("pigletFeedProductionForm.question.dieselConsumptionLHa")}
                    suffix="liter / ha"
                    tooltip={t("pigletFeedProductionForm.tooltips.dieselConsumption")}
                    value={formData.dieselConsumptionLHa}
                    initialValue={formData.dieselConsumptionLHa}
                    onChange={(v) => updateFormData("dieselConsumptionLHa", v)}
                    rules={[
                      {
                        required: true,
                        validator: (_: unknown, v: number) => {
                          if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                            return Promise.reject(new Error(t("pigletFeedProductionForm.error.validNumber")));
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  />
                </Form.Item>
                <Typography variant="paragraph-s" color="grey-600" className="margin-top-25 margin-left-50">
                  {t("pigletFeedProductionForm.description.dieselConsumption")}
                </Typography>

                <Form.Item>
                  <InputNumber
                    disabled={readOnly}
                    dataTestId="energy-used-input"
                    name="energyUsedKwhHa"
                    label={t("pigletFeedProductionForm.question.energyUsedKwhHa")}
                    suffix="kWh / ha"
                    tooltip={t("pigletFeedProductionForm.tooltips.energyUsed")}
                    value={formData.energyUsedKwhHa}
                    initialValue={formData.energyUsedKwhHa}
                    onChange={(v) => updateFormData("energyUsedKwhHa", v)}
                    rules={[
                      {
                        required: true,
                        validator: (_: unknown, v: number) => {
                          if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                            return Promise.reject(new Error(t("pigletFeedProductionForm.error.validNumber")));
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  />
                </Form.Item>
              </Card>

              {/* Products Applied */}
              <Card className="feed-card padding-100">
                <Typography variant="h5" className="margin-bottom-175">
                  {t("pigletFeedProductionForm.productsApplied")}
                </Typography>

                <Flex vertical gap={16}>
                  <Form.Item>
                    <InputNumber
                      disabled={readOnly}
                      dataTestId="mineral-nitrogen-input"
                      name="mineralNitrogenKgHa"
                      label={t("pigletFeedProductionForm.question.mineralNitrogenKgHa")}
                      suffix="kg / ha"
                      tooltip={t("pigletFeedProductionForm.tooltips.mineralNitrogen")}
                      value={formData.mineralNitrogenKgHa}
                      initialValue={formData.mineralNitrogenKgHa}
                      onChange={(v) => updateFormData("mineralNitrogenKgHa", v)}
                      rules={[
                        {
                          required: true,
                          validator: (_: unknown, v: number) => {
                            if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                              return Promise.reject(new Error(t("pigletFeedProductionForm.error.validNumber")));
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    />
                  </Form.Item>

                  <Form.Item>
                    <InputNumber
                      disabled={readOnly}
                      dataTestId="mineral-phosphorus-input"
                      name="mineralPhosphorusKgHa"
                      label={t("pigletFeedProductionForm.question.mineralPhosphorusKgHa")}
                      suffix="kg / ha"
                      tooltip={t("pigletFeedProductionForm.tooltips.mineralPhosphorus")}
                      value={formData.mineralPhosphorusKgHa}
                      initialValue={formData.mineralPhosphorusKgHa}
                      onChange={(v) => updateFormData("mineralPhosphorusKgHa", v)}
                      rules={[
                        {
                          required: true,
                          validator: (_: unknown, v: number) => {
                            if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                              return Promise.reject(new Error(t("pigletFeedProductionForm.error.validNumber")));
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    />
                  </Form.Item>

                  <Form.Item>
                    <InputNumber
                      disabled={readOnly}
                      dataTestId="mineral-potassium-input"
                      name="mineralPotassiumKgHa"
                      label={t("pigletFeedProductionForm.question.mineralPotassiumKgHa")}
                      suffix="kg / ha"
                      tooltip={t("pigletFeedProductionForm.tooltips.mineralPotassium")}
                      value={formData.mineralPotassiumKgHa}
                      initialValue={formData.mineralPotassiumKgHa}
                      onChange={(v) => updateFormData("mineralPotassiumKgHa", v)}
                      rules={[
                        {
                          required: true,
                          validator: (_: unknown, v: number) => {
                            if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                              return Promise.reject(new Error(t("pigletFeedProductionForm.error.validNumber")));
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    />
                  </Form.Item>

                  <Form.Item>
                    <InputNumber
                      disabled={readOnly}
                      dataTestId="organic-fertilizer-input"
                      name="nitrogenOrganicFertKgHa"
                      label={t("pigletFeedProductionForm.question.nitrogenOrganicFertKgHa")}
                      suffix="kg / ha"
                      tooltip={t("pigletFeedProductionForm.tooltips.nitrogenOrganicFert")}
                      value={formData.nitrogenOrganicFertKgHa}
                      initialValue={formData.nitrogenOrganicFertKgHa}
                      onChange={(v) => updateFormData("nitrogenOrganicFertKgHa", v)}
                      rules={[
                        {
                          required: true,
                          validator: (_: unknown, v: number) => {
                            if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                              return Promise.reject(new Error(t("pigletFeedProductionForm.error.validNumber")));
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    />
                  </Form.Item>

                  <Form.Item>
                    <InputNumber
                      disabled={readOnly}
                      dataTestId="lime-applied-input"
                      name="totalLimeAppliedKgCaCo3PerHa"
                      label={t("pigletFeedProductionForm.question.totalLimeAppliedKgCaCo3PerHa")}
                      suffix="kg CaCO₃ / ha"
                      tooltip={t("pigletFeedProductionForm.tooltips.totalLimeApplied")}
                      value={formData.totalLimeAppliedKgCaCo3PerHa}
                      initialValue={formData.totalLimeAppliedKgCaCo3PerHa}
                      onChange={(v) => updateFormData("totalLimeAppliedKgCaCo3PerHa", v)}
                      rules={[
                        {
                          required: true,
                          validator: (_: unknown, v: number) => {
                            if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                              return Promise.reject(new Error(t("pigletFeedProductionForm.error.validNumber")));
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    />
                  </Form.Item>

                  <Form.Item>
                    <InputNumberWithUnitSelector
                      disabled={readOnly}
                      dataTestId="plant-protection-input"
                      name="totalPlantProtectionApplied"
                      label={t("pigletFeedProductionForm.question.totalPlantProtectionApplied")}
                      unitLabel={t("unit", { ns: "footprinting" })}
                      units={Object.values(DAIRY_FEED_PRODUCTION_UNITS)}
                      tooltip={t("pigletFeedProductionForm.tooltips.totalPlantProtectionApplied")}
                      value={formData.totalPlantProtectionApplied}
                      initialValue={formData.totalPlantProtectionApplied}
                      unitValue={formData.totalPlantProtectionAppliedUnit}
                      onChange={(v, unit) => {
                        const nextValue = v ?? formData.totalPlantProtectionApplied;
                        const nextUnit = unit as ENUM_DAIRY_FEED_PRODUCTION_UNITS;
                        setFormData((prev) => ({
                          ...prev!,
                          totalPlantProtectionApplied: nextValue,
                          totalPlantProtectionAppliedUnit: nextUnit,
                        }));
                        // Set synchronously (rather than via a separate effect+timer) since the unit field
                        // is a sibling Form field: Ant Design only auto-revalidates a field on its own change.
                        const error = getPlantProtectionError(nextValue, nextUnit);
                        formRef.current?.setFields([{ name: "totalPlantProtectionApplied", errors: error ? [error] : [] }]);
                      }}
                      validateFirst
                      rules={[
                        {
                          required: true,
                          validator: (_: unknown, v: number) => {
                            const error = getPlantProtectionError(v, formData.totalPlantProtectionAppliedUnit);
                            return error ? Promise.reject(new Error(error)) : Promise.resolve();
                          },
                        },
                      ]}
                    />
                  </Form.Item>
                </Flex>
              </Card>

              {/* Irrigation */}
              <Card className="feed-card padding-100">
                <Typography variant="h5" className="margin-bottom-175">
                  {t("pigletFeedProductionForm.irrigation")}
                </Typography>
                <Form.Item>
                  <InputNumber
                    disabled={readOnly}
                    dataTestId="water-irrigation-input"
                    name="totalWaterUsedIrregationLPerHa"
                    label={t("pigletFeedProductionForm.question.totalWaterUsedIrregationLPerHa")}
                    suffix="liter / ha"
                    tooltip={t("pigletFeedProductionForm.tooltips.totalWaterUsedIrregation")}
                    value={formData.totalWaterUsedIrregationLPerHa}
                    initialValue={formData.totalWaterUsedIrregationLPerHa}
                    onChange={(v) => updateFormData("totalWaterUsedIrregationLPerHa", v)}
                    rules={[
                      {
                        required: true,
                        validator: (_: unknown, v: number) => {
                          if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                            return Promise.reject(new Error(t("pigletFeedProductionForm.error.validNumber")));
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  />
                </Form.Item>
              </Card>
            </Flex>

            {/* Crop Usage Radio */}
            <Card className="padding-100 feed-card">
              <Typography variant="h5" className="margin-bottom-100">
                {t("pigletFeedProductionForm.question.cropUsageRadioTitle")}
              </Typography>
              <Flex align="center" gap={16} justify="space-between">
                <Typography variant="paragraph-m" style={{ maxWidth: "60%" }}>
                  {t("pigletFeedProductionForm.question.cropUsageRadioDescription")}
                  <span className="required-asterisk">*</span>
                </Typography>
                <KlimRadioGroup
                  required
                  name="cropUsedForFeed"
                  dataTestId="crop-used-for-feed-radio"
                  disabled={readOnly}
                  vertical={false}
                  type="tertiary"
                  size="large"
                  onChange={({ target }) => updateFormData("cropUsedForFeed", target.value)}
                  value={formData.cropUsedForFeed}
                  optionType="button"
                  options={[FARM_FOOTPRINTING_DAIRY_FEED_PRODUCTION_CROP_USAGE.YES, FARM_FOOTPRINTING_DAIRY_FEED_PRODUCTION_CROP_USAGE.NO].map((usage) => ({
                    label: t(`pigletFeedProductionForm.cropUsageOptions.${usage}`),
                    value: usage,
                  }))}
                />
              </Flex>

              {/* Production Fields - Show if NO */}
              {formData.cropUsedForFeed === FARM_FOOTPRINTING_DAIRY_FEED_PRODUCTION_CROP_USAGE.NO && (
                <Flex vertical gap={16} className="margin-top-150">
                  <Form.Item>
                    <InputNumber
                      disabled={readOnly}
                      dataTestId="sold-off-farm-input"
                      name="soldOffFarmPct"
                      label={t("pigletFeedProductionForm.question.soldOffFarmPct")}
                      suffix={t("pigletFeedProductionForm.question.totalPctSuffix")}
                      tooltip={t("pigletFeedProductionForm.tooltips.soldOffFarm")}
                      value={formatFractionInput(formData.soldOffFarmPct)}
                      initialValue={formatFractionInput(formData.soldOffFarmPct)}
                      onChange={(v) => updateFormData("soldOffFarmPct", formatPercentageInput(v))}
                      precision={2}
                      rules={[
                        {
                          required: true,
                          validator: (_: unknown, v: number) => {
                            if (isNullOrUndefined(v) || !isValidPercentageDecimal(v)) {
                              return Promise.reject(new Error(t("pigletFeedProductionForm.error.validPercentage")));
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    />
                  </Form.Item>

                  <Form.Item>
                    <InputNumber
                      disabled={readOnly}
                      dataTestId="fed-to-piglets-input"
                      name="fedToPigletsPct"
                      label={t("pigletFeedProductionForm.question.fedToPigletsPct")}
                      suffix={t("pigletFeedProductionForm.question.totalPctSuffix")}
                      tooltip={t("pigletFeedProductionForm.tooltips.fedToPiglets")}
                      value={formatFractionInput(formData.fedToPigletsPct)}
                      initialValue={formatFractionInput(formData.fedToPigletsPct)}
                      onChange={(v) => updateFormData("fedToPigletsPct", formatPercentageInput(v))}
                      precision={2}
                      rules={[
                        {
                          required: true,
                          validator: (_: unknown, v: number) => {
                            if (isNullOrUndefined(v) || !isValidPercentageDecimal(v)) {
                              return Promise.reject(new Error(t("pigletFeedProductionForm.error.validPercentage")));
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    />
                  </Form.Item>

                  <Form.Item>
                    <InputNumber
                      disabled={readOnly}
                      dataTestId="other-uses-input"
                      name="otherUsesPct"
                      label={t("pigletFeedProductionForm.question.otherUsesPct")}
                      suffix={t("pigletFeedProductionForm.question.totalPctSuffix")}
                      tooltip={t("pigletFeedProductionForm.tooltips.otherUses")}
                      value={formatFractionInput(formData.otherUsesPct)}
                      initialValue={formatFractionInput(formData.otherUsesPct)}
                      onChange={(v) => updateFormData("otherUsesPct", formatPercentageInput(v))}
                      precision={2}
                      rules={[
                        {
                          required: true,
                          validator: (_: unknown, v: number) => {
                            if (isNullOrUndefined(v) || !isValidPercentageDecimal(v)) {
                              return Promise.reject(new Error(t("pigletFeedProductionForm.error.validPercentage")));
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    />
                  </Form.Item>
                </Flex>
              )}
            </Card>
          </Flex>
        </Form>
        <Button className="margin-top-200" type="primary" onClick={handleSave} loading={saving} disabled={readOnly} dataTestId="save-button">
          {t("pigletFeedProductionForm.saveAndClose")}
        </Button>
      </Flex>
    </Drawer>
  );
};
